import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { appConfig } from "~/config/app";
import { pathsConfig } from "~/config/paths";
import {
  appendCheckoutIntentToPath,
  getCheckoutIntentFromPricingSelection,
  persistCheckoutIntent,
} from "~/modules/billing/lib/checkout-intent";

import type { User } from "@workspace/auth";
import type { BillingConfigPlan } from "@workspace/billing";

export const usePlan = (plan: BillingConfigPlan) => {
  const router = useRouter();

  const toFeatureKey = (feature: string) => {
    const parts = feature.toLowerCase().split("_");
    const [first, ...rest] = parts;
    const camel = [
      first,
      ...rest.map((p) => (p ? p[0]!.toUpperCase() + p.slice(1) : p)),
    ].join("");

    return `feature.${camel}`;
  };

  const features = plan.features.map((feature) => ({
    id: feature,
    available: true,
    key: toFeatureKey(feature),
  }));

  const handleCheckout = ({
    variant,
    user,
  }: {
    variant: { id: string; interval?: string | null };
    user: User | null;
  }) => {
    const checkoutIntent = getCheckoutIntentFromPricingSelection({
      planId: plan.id,
      interval: variant.interval,
    });

    if (checkoutIntent) {
      persistCheckoutIntent(checkoutIntent);
    }

    if (!user) {
      const url = new URL(pathsConfig.auth.login, appConfig.url);
      url.searchParams.set(
        "redirectTo",
        appendCheckoutIntentToPath({
          path: pathsConfig.dashboard.user.index,
          checkoutIntent,
        }),
      );
      return router.push(url.toString());
    }

    toast.info(
      "Choose an organization to continue with your selected trial plan.",
    );

    return router.push(
      appendCheckoutIntentToPath({
        path: pathsConfig.dashboard.user.index,
        checkoutIntent,
      }),
    );
  };

  const handleOpenPortal = ({ user }: { user: User | null }) => {
    if (!user) {
      const url = new URL(pathsConfig.auth.login, appConfig.url);
      url.searchParams.set("redirectTo", pathsConfig.marketing.pricing);
      return router.push(url.toString());
    }

    toast.info(
      "Organization billing controls live inside each organization's Billing settings.",
    );
    return router.push(pathsConfig.dashboard.user.settings.billing);
  };

  return {
    isPending: false,
    // price,
    features,
    // discount,
    handleCheckout,
    handleOpenPortal,
  };
};
